import { VERSION_ID_MAP, VersionEnum } from '@gekichumai/dxdata'
import * as Sentry from '@sentry/react'
import { useMemo } from 'react'
import type { Region } from '../../models/context/AppContext'
import { useRatingCalculatorContext } from '../../models/context/RatingCalculatorContext'
import { useAppContext, useAppContextDXDataVersion } from '../../models/context/useAppContext'
import type { Entry } from '../../pages/RatingCalculator'
import { type FlattenedSheet, useSheets } from '../../songs'
import { calculateRating, type Rating } from '../../utils/rating'

export type RatingCalculatorEntry = Entry & {
  sheet: FlattenedSheet
  rating: Rating | null
}
interface UseRatingEntriesReturn {
  allEntries: RatingCalculatorEntry[]
  b15Entries: RatingCalculatorEntry[]
  b35Entries: RatingCalculatorEntry[]
  statistics: UseRatingEntriesStatistics
}
interface UseRatingEntriesStatistics {
  b15Average: number
  b35Average: number
  b15Min: number
  b35Min: number
  b15Max: number
  b35Max: number
  b15Sum: number
  b35Sum: number
  b50Sum: number
}

const filterEligibleB15Entries = (sheet: FlattenedSheet, appVersion: VersionEnum, region: Region) => {
  if (region === '_generic') return sheet.version === appVersion

  const appVersionId = VERSION_ID_MAP.get(appVersion)
  const sheetVersionId = VERSION_ID_MAP.get(sheet.version)
  if (appVersionId !== undefined && sheetVersionId !== undefined) {
    const useCircleB15 = appVersionId >= VERSION_ID_MAP.get(VersionEnum.CiRCLE)!
    const sheetIsAvailableInRegion = sheet.regions[region]
    if (useCircleB15) {
      return sheetIsAvailableInRegion && (appVersionId === sheetVersionId || appVersionId === sheetVersionId + 1)
    }

    return sheetIsAvailableInRegion && appVersionId === sheetVersionId // only consider a B15 match if sheet version equals to app version, and sheet is available in the app region
  }
  return false
}

const filterB35EligibleEntries = (sheet: FlattenedSheet, appVersion: VersionEnum, region: Region) => {
  if (region === '_generic') return sheet.version !== appVersion

  const appVersionId = VERSION_ID_MAP.get(appVersion)
  const sheetVersionId = VERSION_ID_MAP.get(sheet.version)
  if (appVersionId !== undefined && sheetVersionId !== undefined) {
    const useCircleB15 = appVersionId >= VERSION_ID_MAP.get(VersionEnum.CiRCLE)!
    const sheetIsAvailableInRegion = sheet.regions[region]
    if (useCircleB15) {
      return sheetIsAvailableInRegion && appVersionId > sheetVersionId + 1
    }

    return sheetIsAvailableInRegion && appVersionId > sheetVersionId // only consider a B35 match if app version is greater than sheet version, and sheet is available in the app region
  }
  return false
}

export const useRatingEntries = (): UseRatingEntriesReturn => {
  const appVersion = useAppContextDXDataVersion()
  const { region } = useAppContext()
  const { entries } = useRatingCalculatorContext()
  const { data: sheets } = useSheets({ acceptsPartialData: true })

  const { allEntries, b15Entries, b35Entries } = useMemo(() => {
    const computeStart = performance.now()
    const calculated = entries.flatMap((entry) => {
      const sheet = sheets?.find((sheet) => sheet.id === entry.sheetId)
      if (!sheet) {
        return []
      }

      return [
        {
          ...entry,
          sheet,
          rating: sheet.isRatingEligible
            ? calculateRating(sheet.internalLevelValue, entry.achievementRate, entry.comboFlag)
            : null,
        },
      ]
    })

    const best15OfCurrentVersionSheetIds = calculated
      .filter((entry) => filterEligibleB15Entries(entry.sheet, appVersion, region))
      // a.rating and b.rating could be null. put them at the end
      .sort((a, b) => {
        if (!a.rating) return 1
        if (!b.rating) return -1
        return b.rating.ratingAwardValue - a.rating.ratingAwardValue
      })
      .slice(0, 15)
      .map((entry) => entry.sheetId)

    const best35OfAllOtherVersionSheetIds = calculated
      .filter((entry) => filterB35EligibleEntries(entry.sheet, appVersion, region))
      .sort((a, b) => {
        if (!a.rating) return 1
        if (!b.rating) return -1
        return b.rating.ratingAwardValue - a.rating.ratingAwardValue
      })
      .slice(0, 35)
      .map((entry) => entry.sheetId)

    console.log('best15OfCurrentVersionSheetIds', best15OfCurrentVersionSheetIds)
    console.log('best35OfAllOtherVersionSheetIds', best35OfAllOtherVersionSheetIds)

    const calculatedEntries = calculated.map((entry) => ({
      ...entry,
      includedIn:
        best15OfCurrentVersionSheetIds.includes(entry.sheetId) ||
        (region === 'cn' && entry.providerConfig?.divingFish?.ratingEligibility === 'b15')
          ? ('b15' as const)
          : best35OfAllOtherVersionSheetIds.includes(entry.sheetId) ||
              (region === 'cn' && entry.providerConfig?.divingFish?.ratingEligibility === 'b35')
            ? ('b35' as const)
            : null,
    }))

    Sentry.metrics.distribution('rating_calculation.duration', performance.now() - computeStart, {
      unit: 'millisecond',
      attributes: { entry_count: String(entries.length) },
    })

    return {
      allEntries: calculatedEntries,
      b15Entries: calculatedEntries.filter((entry) => entry.includedIn === 'b15'),
      b35Entries: calculatedEntries.filter((entry) => entry.includedIn === 'b35'),
    }
  }, [entries, sheets, appVersion, region])

  const statistics = useMemo(() => {
    const eligibleRatingEntriesB15 = b15Entries.filter((entry) => entry.rating)
    const eligibleRatingEntriesB35 = b35Entries.filter((entry) => entry.rating)

    const b15Average =
      eligibleRatingEntriesB15.reduce((acc, entry) => acc + entry.rating!.ratingAwardValue, 0) / b15Entries.length

    const b35Average =
      eligibleRatingEntriesB35.reduce((acc, entry) => acc + entry.rating!.ratingAwardValue, 0) / b35Entries.length

    const b15Min = Math.min(...eligibleRatingEntriesB15.map((entry) => entry.rating!.ratingAwardValue))
    const b35Min = Math.min(...eligibleRatingEntriesB35.map((entry) => entry.rating!.ratingAwardValue))

    const b15Max = Math.max(...eligibleRatingEntriesB15.map((entry) => entry.rating!.ratingAwardValue))
    const b35Max = Math.max(...eligibleRatingEntriesB35.map((entry) => entry.rating!.ratingAwardValue))

    const b15Sum = eligibleRatingEntriesB15.reduce((acc, entry) => acc + entry.rating!.ratingAwardValue, 0)
    const b35Sum = eligibleRatingEntriesB35.reduce((acc, entry) => acc + entry.rating!.ratingAwardValue, 0)

    const b50Sum = b15Sum + b35Sum

    return {
      b15Average,
      b35Average,
      b15Min,
      b35Min,
      b15Max,
      b35Max,
      b15Sum,
      b35Sum,
      b50Sum,
    }
  }, [b15Entries, b35Entries])

  return { allEntries, b15Entries, b35Entries, statistics }
}